import React, { useState, useContext } from 'react';
import { BackIcon, ICON_SIZE, SendIcon } from "./actionIcons";
import { Card, Title, Text, TextInput } from '@tremor/react';
import { GlobalContext } from "./GlobalContext";
import { useNavigate } from 'react-router-dom';
import { formatPhoneForStorage } from './UtilityFunctions';
import fields from './fields';
import { normalizeFieldValue, checkFieldValue } from './validation/fieldCheck';
import { apiFetch } from './api.js';
import FullScreenScroll from './FullScreenScroll';

function InviteRequestForm() {
  const navigate = useNavigate();
  const { toggleMessageBox } = useContext(GlobalContext);

  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    dealershipName: ''
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    let newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = "Full name is required";
    //The shared rule, not a fourth hand-written copy of it. This inline
    ///^0[0-9]{9}$/ was one of the places that accepted 0070000000, and an
    //inline copy is what let it drift from fields.js in the first place.
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    else {
      const phoneFailure = checkFieldValue(
        normalizeFieldValue(formData.phone, fields.Phone1), fields.Phone1);
      if (phoneFailure) newErrors.phone = phoneFailure.message;
    }
    if (!formData.email.trim()) newErrors.email = "Email is required";
      else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Invalid email format";
    if (!formData.dealershipName.trim()) newErrors.dealershipName = "Dealership name is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const res = await apiFetch('/admin/invite/request', { body: {
          ...formData,
          phone: formatPhoneForStorage(normalizeFieldValue(formData.phone, fields.Phone1))
        } });

      const data = await res.json();

      if (res.ok) {
        toggleMessageBox({
          on: true,
          title: "Request Submitted",
          message: "Your invite request has been sent. We will review it and email you the signup link if approved.",
          buttons: ["OK"],
          clicked: ""
        });
        navigate("/login");
      } else {
        toggleMessageBox({
          on: true,
          title: "Error",
          message: data.message || "Failed to submit request. Please try again later.",
          buttons: ["OK"],
          clicked: ""
        });
      }

    } catch (err) {
      toggleMessageBox({
        on: true,
        title: "Network Error",
        message: "Unable to send request. Please check your internet connection.",
        buttons: ["OK"],
        clicked: ""
      });
    }
  };

  return (
    <FullScreenScroll>
      <Card className="max-w-lg mx-auto p-6">
        {/* Header with Back to Login */}
        <div className="flex justify-between items-center mb-6">
          <div></div>
          <Title className="text-center">Request an Invite</Title>
          <button
            className="btn-t btn-t-secondary"
            onClick={() => navigate("/login")}
          ><BackIcon size={ICON_SIZE} />
            Back to Login
          </button>
        </div>

        <Text className="mb-6">
          Fill in your details below to request an invite to create a dealership account.
        </Text>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Text>Full Name</Text>
            <TextInput
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="Enter your full name"
              error={!!errors.fullName}
            />
            {errors.fullName && <Text color="red" className="text-sm">{errors.fullName}</Text>}
          </div>

          <div>
            <Text>Phone Number</Text>
            <TextInput
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Enter your phone number"
              error={!!errors.phone}
            />
            {errors.phone && <Text color="red" className="text-sm">{errors.phone}</Text>}
          </div>

          <div>
            <Text>Email Address</Text>
            <TextInput
              name="email"
              value={formData.email}
              onChange={handleChange}
              type="email"
              placeholder="Enter your email address"
              error={!!errors.email}
            />
            {errors.email && <Text color="red" className="text-sm">{errors.email}</Text>}
          </div>

          <div>
            <Text>Dealership Name</Text>
            <TextInput
              name="dealershipName"
              value={formData.dealershipName}
              onChange={handleChange}
              placeholder="Enter dealership name"
              error={!!errors.dealershipName}
            />
            {errors.dealershipName && <Text color="red" className="text-sm">{errors.dealershipName}</Text>}
          </div>

          {/* .btn-t so this matches the Sign in button on LogIn; TremorButton
              renders its own blue regardless of the theme. */}
          <button type="submit" className="btn-t btn-t-primary w-full mt-4"><SendIcon size={ICON_SIZE} />
            Submit Request
          </button>
        </form>
      </Card>
    </FullScreenScroll>
  );
}

export default InviteRequestForm;
